import { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Building2, ShieldAlert, ShieldCheck, Shield, ShieldX, CheckCircle2, Clock, AlertTriangle, FileQuestion } from 'lucide-react';
import OTDDrilldownModal from '../../components/drilldown/OTDDrilldownModal';
import OTIFDrilldownModal from '../../components/drilldown/OTIFDrilldownModal';
import RatingBreakdownModal from '../../components/drilldown/RatingBreakdownModal';
import { SupplierWithStatus, SupplierStatus } from './types';
import { STATUS_CONFIG } from './constants';
import { getRiskLevelStyle } from './utils';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { getGradeInfo, ratingBadgeStyle } from '../supplier-performance/utils';
import { useRatingBands } from '../../hooks/useRatingBands';
import { useOTIFBands, toDeliveryBands } from '../../hooks/useOTIFBands';
import { getDeliveryColor } from '../../components/dashboard/SiteKPIComponents';
import { useFormatters } from '../../contexts/PreferencesContext';

type QuestionnaireOverall = 'up_to_date' | 'expiring' | 'overdue' | 'pending' | 'none';

const QUESTIONNAIRE_BADGE_CONFIG: Record<QuestionnaireOverall, { label: string; className: string; Icon: typeof CheckCircle2 }> = {
  up_to_date: {
    label: 'Up to Date',
    className: 'bg-green-50 text-green-700 border-green-200',
    Icon: CheckCircle2,
  },
  expiring: {
    label: 'Expiring Soon',
    className: 'bg-amber-50 text-amber-700 border-amber-200',
    Icon: Clock,
  },
  overdue: {
    label: 'Overdue',
    className: 'bg-red-50 text-red-700 border-red-200',
    Icon: AlertTriangle,
  },
  pending: {
    label: 'Pending',
    className: 'bg-gray-50 text-gray-700 border-gray-200',
    Icon: FileQuestion,
  },
  none: {
    label: 'Not Assigned',
    className: 'bg-gray-50 text-gray-500 border-gray-200',
    Icon: FileQuestion,
  },
};

interface SupplierCardProps {
  supplier: SupplierWithStatus;
  onNavigate: (path: string) => void;
}

export function SupplierCard({ supplier, onNavigate }: SupplierCardProps) {
  const { isSupplierCardFieldEnabled } = useMenuSettings();
  const { formatDate } = useFormatters();
  // Customer review 11 Sep (item 2): the configured Grade Thresholds, so
  // this badge and the Supplier Performance table agree on one score's grade.
  const { bands: ratingBands } = useRatingBands();
  // CR-6269: the delivery rate is coloured on the OTIF Grade Thresholds, as the dashboard tiles are.
  const { bands: otifBands } = useOTIFBands();
  const status = supplier.onboarding_status_derived || 'draft';
  // CR-5557: clicking the OTD tile opens the drill-through directly (does
  // not navigate to the detail page) so users can validate the rate from
  // the supplier list without an extra hop.
  const [otdDrilldownOpen, setOtdDrilldownOpen] = useState(false);
  // CR-8208: the OTIF tile opens the OTIF drill-down (PO line suppliers).
  const [otifDrilldownOpen, setOtifDrilldownOpen] = useState(false);
  // Rating breakdown modal - shows how the combined score is calculated
  const [ratingBreakdownOpen, setRatingBreakdownOpen] = useState(false);

  const showLogo = isSupplierCardFieldEnabled('logo');
  const showRisk = isSupplierCardFieldEnabled('risk_badge');
  // CR-8208: Delivery Metric Mode picks the tiles (Both / OTD Only / OTIF
  // Only); each tile is also subject to its own Supplier Card element.
  const deliveryMode = supplier.delivery_metric_mode ?? 'both';
  const showOtd = isSupplierCardFieldEnabled('otd') && deliveryMode !== 'otif_only';
  const showOtif = isSupplierCardFieldEnabled('otif') && deliveryMode !== 'otd_only';
  // CR-8209: the window both tiles and both click throughs use.
  const cardWindow = supplier.card_window_start && supplier.card_window_end
    ? { date_from: supplier.card_window_start, date_to: supplier.card_window_end }
    : {};
  const windowTitle = supplier.card_window_start && supplier.card_window_end
    ? `${formatDate(supplier.card_window_start)} to ${formatDate(supplier.card_window_end)}`
    : undefined;
  // A schedule line supplier's OTIF has no matching drill-down yet (CR-8212).
  const otifClickable = supplier.otif_grain === 'line' && supplier.otif_rate !== null && supplier.otif_rate !== undefined;
  const showRating = isSupplierCardFieldEnabled('rating');
  const showQuestionnaireStatus = isSupplierCardFieldEnabled('questionnaire_status');

  const getActionButton = () => {
    switch (status) {
      case 'approved':
      case 'due_diligence':
        // Approved suppliers now surface questionnaire state via the badge below,
        // not via a button. Workflow buttons only appear for non-approved states.
        return null;
      case 'not_approved':
        return (
          <Button
            size="default"
            variant="outline"
            className="text-sm font-medium bg-red-50 text-red-700 border-red-200 hover:bg-red-100 px-4"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(`/suppliers/wizard/${supplier.company_id}`);
            }}
          >
            Re-open Approval Request
          </Button>
        );
      case 'pending_initial':
        return (
          <Button
            size="default"
            variant="outline"
            className="text-sm font-medium bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100 px-4"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(`/onboarding?review=${supplier.id}`);
            }}
          >
            Review Approval
          </Button>
        );
      case 'invited':
        return (
          <Button
            size="default"
            variant="outline"
            className="text-sm font-medium bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100 px-4"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(`/suppliers/wizard/${supplier.company_id}`);
            }}
          >
            View Approval Request
          </Button>
        );
      case 'draft':
        return (
          <Button
            size="default"
            variant="outline"
            className="text-sm font-medium bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 px-4"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(`/suppliers/wizard/${supplier.company_id}`);
            }}
          >
            Continue Setup
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <>
    <Card
      className="hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => {
        // CR-4579: Supplier groups navigate to group detail page
        if ((supplier as unknown as Record<string, unknown>)['is_supplier_group']) {
          const groupId = (supplier as unknown as Record<string, unknown>)['group_id'];
          if (groupId) onNavigate(`/suppliers/groups/${groupId}`);
          return;
        }
        if (supplier.company_id) onNavigate(`/suppliers/${supplier.company_id}`);
      }}
    >
      <CardContent className="p-4 text-center space-y-3">
        {/* Company Logo/Icon */}
        {showLogo && (
          <div className="flex justify-center">
            <div className="w-full h-32 rounded-md flex items-center justify-center bg-gray-50/50 p-2">
              {(supplier.company_details as unknown as Record<string, unknown>)?.['logo_url'] ? (
                <img
                  src={(supplier.company_details as unknown as Record<string, unknown>)['logo_url'] as string}
                  alt={supplier.company_name}
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <Building2 className="h-14 w-14 text-gray-300" />
              )}
            </div>
          </div>
        )}

        {/* Company Name */}
        <div className="min-h-[28px] flex items-center justify-center">
          <h3 className="text-sm font-semibold line-clamp-2 leading-tight" title={supplier.company_name}>
            {supplier.company_name}
          </h3>
        </div>

        {/* Deactivated suppliers only appear when "Include Inactive" is on.
            Mark them plainly — an unmarked inactive supplier reads as a live
            one, and its code is absent from the ERP feed filter. */}
        {supplier.company_details?.is_active === false ? (
          <div className="flex items-center justify-center">
            <Badge
              variant="outline"
              className="text-xs font-medium px-2.5 py-1 bg-red-50 text-red-700 border-red-200"
              title="Deactivated — excluded from the ERP supplier feed filter"
            >
              Inactive
            </Badge>
          </div>
        ) : supplier.relationship_is_active === false && (
          /* V9: switched off for this company only — the supplier is still
             live for other customers, so say which kind of inactive this is. */
          <div className="flex items-center justify-center">
            <Badge
              variant="outline"
              className="text-xs font-medium px-2.5 py-1 bg-red-50 text-red-700 border-red-200"
              title="Deactivated for your company by a customer admin; still active for other customers"
            >
              Inactive (your company)
            </Badge>
          </div>
        )}

        {/* CR-4497 + CR-4772: Risk Level + Credit Score badges */}
        {((showRisk && supplier.risk_level && supplier.risk_level !== 'none')
          || (typeof supplier.latest_credit_score === 'number')) && (
          <div className="flex items-center justify-center gap-1.5 flex-wrap">
            {showRisk && supplier.risk_level && supplier.risk_level !== 'none' && (
              <Badge className={`text-xs font-medium px-2.5 py-1 ${getRiskLevelStyle(supplier.risk_level).badgeColor}`}>
                {getRiskLevelStyle(supplier.risk_level).icon === 'critical' ? (
                  <ShieldX className="h-3.5 w-3.5 mr-1" />
                ) : getRiskLevelStyle(supplier.risk_level).icon === 'high' ? (
                  <ShieldAlert className="h-3.5 w-3.5 mr-1" />
                ) : getRiskLevelStyle(supplier.risk_level).icon === 'low' ? (
                  <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                ) : (
                  <Shield className="h-3.5 w-3.5 mr-1" />
                )}
                {getRiskLevelStyle(supplier.risk_level).label}
              </Badge>
            )}
            {typeof supplier.latest_credit_score === 'number' && (() => {
              // CR-4772: Credit score banding mirrors ComplianceTab's distribution cuts
              // (Excellent 800+, Good 700-799, Fair 600-699, Poor <600). Styling is
              // parallel to the risk badge — colour encodes the tier.
              const score = supplier.latest_credit_score;
              const band = score >= 800
                ? { label: 'Excellent', cls: 'bg-green-50 text-green-700 border-green-200' }
                : score >= 700
                  ? { label: 'Good', cls: 'bg-blue-50 text-blue-700 border-blue-200' }
                  : score >= 600
                    ? { label: 'Fair', cls: 'bg-amber-50 text-amber-700 border-amber-200' }
                    : { label: 'Poor', cls: 'bg-red-50 text-red-700 border-red-200' };
              return (
                <Badge className={`text-xs font-medium px-2.5 py-1 ${band.cls}`} title={`Credit Score ${score} — ${band.label}`}>
                  Credit {score}
                </Badge>
              );
            })()}
          </div>
        )}

        {/* CR-4475: Key metrics for approved suppliers */}
        {status === 'approved' && (showOtd || showOtif || showRating) && (
          // CR-8208: OTD and OTIF share a row; with all three, Rating takes the row below.
          <div className={`grid ${[showOtd, showOtif, showRating].filter(Boolean).length > 1 ? 'grid-cols-2' : 'grid-cols-1'} gap-2 text-xs`}>
            {showOtd && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOtdDrilldownOpen(true);
                }}
                className="bg-muted/40 rounded px-2 py-1.5 text-left hover:bg-muted/70 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring"
                title="Click to view the GRNs included in this rate"
                data-testid="supplier-card-otd"
              >
                <span className="text-muted-foreground block text-xs">
                  OTD
                  {/* CR-5557 / CR-8209: the window the rate covers, exact months on hover. */}
                  {supplier.otd_timeframe_label && (
                    <span className="ml-1 opacity-70" title={windowTitle}>· {supplier.otd_timeframe_label}</span>
                  )}
                </span>
                <DeliveryRate rate={supplier.on_time_delivery_rate} otifBands={otifBands} />
              </button>
            )}
            {showOtif && (
              <button
                type="button"
                disabled={!otifClickable}
                onClick={(e) => {
                  e.stopPropagation();
                  if (otifClickable) setOtifDrilldownOpen(true);
                }}
                className={`bg-muted/40 rounded px-2 py-1.5 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-ring ${otifClickable ? 'hover:bg-muted/70 cursor-pointer' : 'cursor-default'}`}
                title={otifClickable ? 'Click to view the PO lines included in this rate' : windowTitle}
                data-testid="supplier-card-otif"
              >
                <span className="text-muted-foreground block text-xs">
                  OTIF
                  {supplier.otd_timeframe_label && (
                    <span className="ml-1 opacity-70" title={windowTitle}>· {supplier.otd_timeframe_label}</span>
                  )}
                </span>
                <DeliveryRate rate={supplier.otif_rate} otifBands={otifBands} />
              </button>
            )}
            {showRating && (() => {
              // Display the performance badge instead of a numeric 0.0 rating:
              // combined_score (0-100) graded on the configured bands.
              const hasScore = supplier.combined_score !== null && supplier.combined_score !== undefined;
              const gradeInfo = getGradeInfo(supplier.combined_score, ratingBands);
              return (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRatingBreakdownOpen(true);
                  }}
                  className={`bg-muted/40 rounded px-2 py-1.5 text-left hover:bg-muted/70 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring ${showOtd && showOtif ? 'col-span-2' : ''}`}
                  title="Click to view rating breakdown"
                >
                  <span className="text-muted-foreground block text-xs">Rating</span>
                  {hasScore ? (
                    <Badge
                      className="font-semibold text-xs mt-0.5"
                      style={{
                        ...ratingBadgeStyle(gradeInfo.color),
                      }}
                    >
                      {gradeInfo.label}
                    </Badge>
                  ) : (
                    <span className="font-semibold text-base text-muted-foreground">N/A</span>
                  )}
                </button>
              );
            })()}
          </div>
        )}

        {/* Questionnaire compliance badge (approved suppliers, only when at least one is assigned) */}
        {status === 'approved' && showQuestionnaireStatus && supplier.questionnaire_status && supplier.questionnaire_status.total > 0 && (() => {
          const qs = supplier.questionnaire_status;
          const cfg = QUESTIONNAIRE_BADGE_CONFIG[qs.overall];
          const fraction = qs.total > 0 ? `${qs.approved - qs.overdue}/${qs.total}` : null;
          const handleClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            if (supplier.company_id) onNavigate(`/suppliers/${supplier.company_id}?tab=questionnaires`);
          };
          return (
            <div className="flex items-center justify-center">
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={handleClick}
                      className="focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full"
                      aria-label={`${cfg.label} questionnaire status — click to view details`}
                    >
                      <Badge variant="outline" className={`text-xs font-medium px-2.5 py-1 gap-1 cursor-pointer hover:shadow-sm transition-shadow ${cfg.className}`}>
                        <cfg.Icon className="h-3.5 w-3.5" />
                        {cfg.label}
                        {fraction && <span className="ml-1 opacity-75">({fraction})</span>}
                      </Badge>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    {qs.items.length === 0 ? (
                      <div className="text-xs">No questionnaires assigned.</div>
                    ) : (
                      <div className="space-y-1">
                        <div className="text-xs font-semibold mb-1">Questionnaire Status</div>
                        {qs.items.map((item, idx) => {
                          const itemCfg = QUESTIONNAIRE_BADGE_CONFIG[item.status];
                          const dateLabel = item.submitted_at
                            ? formatDate(item.submitted_at)
                            : '—';
                          return (
                            <div key={idx} className="flex items-center gap-2 text-xs">
                              <itemCfg.Icon className="h-3 w-3 flex-shrink-0" />
                              <span className="flex-1 truncate">{item.name}</span>
                              <span className="opacity-75">{dateLabel}</span>
                            </div>
                          );
                        })}
                        <div className="text-[10px] opacity-60 pt-1 border-t border-border/40 mt-1">
                          Click to open Questionnaires tab
                        </div>
                      </div>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          );
        })()}

        {/* Action Button based on status (non-approved lifecycle only) */}
        {getActionButton() && (
          <div className="flex items-center justify-center">
            {getActionButton()}
          </div>
        )}

        {/* Progress Bar for Draft status */}
        {status === 'draft' && supplier.draft_progress !== undefined && (
          <div className="space-y-1">
            <Progress value={supplier.draft_progress} className="h-4" />
            <span className="text-xs text-muted-foreground">
              {supplier.draft_progress}% complete
            </span>
          </div>
        )}

        {/* Onboarding Status Badge + V11 approval level (only ever set on approval) */}
        {(status !== 'approved' || supplier.approval_level) && (
          <div className="flex items-center justify-center gap-1.5 flex-wrap">
            {status !== 'approved' && (
              <Badge className={`text-xs font-medium px-2.5 py-1 ${STATUS_CONFIG[status as SupplierStatus]?.badgeColor || 'bg-gray-500 text-white'}`}>
                {STATUS_CONFIG[status as SupplierStatus]?.label || status}
              </Badge>
            )}
            {supplier.approval_level && (
              <Badge
                variant="outline"
                className="text-xs font-medium px-2.5 py-1 border-green-300 text-green-800"
                title={[
                  supplier.approval_level_display || `Level ${supplier.approval_level}`,
                  supplier.approved_at ? `approved on ${formatDate(supplier.approved_at)}` : null,
                  supplier.approved_by_name ? `by ${supplier.approved_by_name}` : null,
                ].filter(Boolean).join(' — ')}
              >
                Level {supplier.approval_level}
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>

    {/* CR-5557: OTD tile drill-through (opened via the OTD button above) */}
    <OTDDrilldownModal
      isOpen={otdDrilldownOpen}
      onClose={() => setOtdDrilldownOpen(false)}
      contextLabel={supplier.company_name}
      filters={{ supplier: supplier.company_id, ...cardWindow }}
    />
    {/* CR-8208: OTIF click through, PO line suppliers, same window and scope as the figure */}
    {otifClickable && (
      <OTIFDrilldownModal
        isOpen={otifDrilldownOpen}
        onClose={() => setOtifDrilldownOpen(false)}
        contextLabel={supplier.company_name}
        filters={{ supplier: supplier.company_id, ...cardWindow }}
        initialTab="total"
      />
    )}
    {/* Rating breakdown modal - shows formula and contributing KPIs */}
    <RatingBreakdownModal
      isOpen={ratingBreakdownOpen}
      onClose={() => setRatingBreakdownOpen(false)}
      supplierName={supplier.company_name}
      combinedScore={supplier.combined_score}
      otifMappedScore={supplier.otif_mapped_score}
      ppmMappedScore={supplier.ppm_mapped_score}
      otifScorePercentage={supplier.otif_score_percentage}
      otifTimeframeLabel={supplier.otif_timeframe_label}
      qualityTimeframeLabel={supplier.quality_timeframe_label}
      otdRate={supplier.on_time_delivery_rate ?? undefined}
      totalPpm={supplier.total_ppm}
      thresholds={ratingBands}
    />
    </>
  );
}

/** One delivery figure on the card: the % coloured on the OTIF grade bands,
 *  or "N/A" when the window has nothing to score (CR-8208 point 3). */
function DeliveryRate({ rate, otifBands }: { rate: number | null | undefined; otifBands: ReturnType<typeof useOTIFBands>['bands'] }) {
  if (rate === null || rate === undefined) {
    return <span className="font-semibold text-base text-muted-foreground">N/A</span>;
  }
  return (
    <span
      className={`font-semibold text-base ${otifBands.length === 0 ? 'text-muted-foreground' : ''}`}
      style={otifBands.length > 0 ? { color: getDeliveryColor(rate, toDeliveryBands(otifBands)) } : undefined}
    >
      {rate.toFixed(2)}%
    </span>
  );
}
